import {useAuthInfo, withRequiredAuthInfo} from "@propelauth/react";
import React, {useContext, useEffect, useState} from "react";
import {useForm} from "react-hook-form";

import {DeveloperProjectConfig, Discoverability} from "../../models/project";
import {DevBidFormConfig} from "./BidCustomDataConf";

import {Tabs, TabsContent, TabsList, TabsTrigger} from "../ui/tabs"
import {Card, CardHeader, CardContent, CardTitle} from "../ui/card";
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from "../ui/form";
import {ListofRounds_v2} from "./RoundConfigrator_v2";
import {Switch} from "../ui/switch";
import {API_URL, fetchWithToken, putWithToken} from "../../api/apiBase";
import {Input} from "../ui/input";
import {useParams} from "react-router-dom";
import {Simulate} from "react-dom/test-utils";
import error = Simulate.error;
import {ProjectContext} from "../../contexts/ProjectContext";
import {CompanyType} from "../../models/company";
import {ProjectBidInfo} from "../builder/ProjectBidInfo";
import {useToast} from "../../hooks/use-toast";
import {useTask} from "../../hooks/useTask";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "../ui/tooltip";
import {InfoCircledIcon, TrashIcon} from "@radix-ui/react-icons";
import {Button} from "../ui/button";

/** leveling_sync task result -> short text, eg '2 placed by AI, 1 not in the workbook' */
const bidTabSummary = (r: any): string | undefined => [
    r?.claude ? `${r.claude} placed by AI` : "",
    r?.unmatched?.length ? `${r.unmatched.length} not matching the workbook` : "",
    r?.relocated?.length ? `${r.relocated.length} moved columns` : "",
    r?.lost?.length ? `${r.lost.length} not found` : "",
    r?.unlinked?.length ? `${r.unlinked.length} not in the workbook` : "",
    r?.claude_errors?.length ? `${r.claude_errors.length} AI batches failed (see logs)` : "",
].filter(Boolean).join(", ") || undefined;


type FormToggleFieldProps = {
    label: string;
    fname: string;
    form: any;
    description?: string;
    variant?: string;
    input_type?: string;
    place_holder?: string;
    disabled?: boolean;
    onChange?: (event: string) => void;
};


export function FormToggleField(
    {label, fname, variant, form, disabled, description}: FormToggleFieldProps) {
    return (
        <FormField
            control={form.control}
            name={fname}
            render={({field}) => (
                <FormItem>
                    <FormControl className="mr-2 my-2">
                        <Switch
                            disabled={disabled}
                            checked={field.value}
                            onCheckedChange={field.onChange}
                        />
                    </FormControl>
                    <FormLabel className="w-full">
                        {description ?

                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger>
                                            <span className="flex items-center gap-3">

                                                    {label}
                                                <InfoCircledIcon width={17} height={17}/>

                                            </span>
                                </TooltipTrigger>
                                <TooltipContent
                                    side={"right"}
                                >
                                    <p>{description}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                            :<>{label}</>
                        }
                    </FormLabel>
                </FormItem>
            )}
        />
    );
}

function ProjectGlobals() {
    const projectContext = useContext(ProjectContext);
    const {accessToken} = useAuthInfo();
    const {project_id} = useParams();
    const {toast} = useToast()

    const form = useForm<DeveloperProjectConfig>(
        {
            defaultValues: {
                ve: {
                    enabled: true,
                    enable_sharing: true,
                    enable_public: false,
                    feedback_enabled: true,
                },
                discoverability: {
                    open_to_all: false,
                    previously_approved: false,
                    invite_only: true,
                },
                // marvin: true,
                scopes_enabled: false,
                smart_feedback: false,
                sharepoint_url: null,
                bid_tab_url: null,
            }
        }
    );

    useEffect(() => {
        fetchWithToken(`/projects/${project_id}/config`, accessToken as string)
            .then((data) => {
                console.log('loaded cfg', data);
                form.reset(data)

            }).catch((err) => {
            toast({
                title: "Error",
                description: "Failed to load Project Settings: " + JSON.stringify(error),
                variant: "destructive",
            })
        }).finally(

        )
    }, [project_id])

    const [toggleDisabled, setToggleDisabled] = useState(false)

    const isA = form.watch('discoverability.open_to_all');
    const isB = form.watch('discoverability.invite_only');
    const isC = form.watch('discoverability.previously_approved');

    useEffect(() => {
        if (isA ) {
          form.setValue('discoverability.invite_only', false);
          form.setValue('discoverability.previously_approved', false);
        }
    }, [isA, form.setValue])

    useEffect(() => {
        if (isB ) {
            form.setValue('discoverability.previously_approved', false);
          form.setValue('discoverability.open_to_all', false);
        }
    }, [isB, form.setValue])

    useEffect(() => {
        if (isC ) {
            form.setValue('discoverability.invite_only', false);
          form.setValue('discoverability.open_to_all', false);
        }
    }, [isC, form.setValue])


    const onSave = (data: DeveloperProjectConfig) => {
        console.log('Save:', data)
        if (accessToken) {
            putWithToken(`/projects/${project_id}/config`, JSON.stringify(data), accessToken)
                .then((data) => {

                }).catch((err) => {
                console.log(JSON.stringify(err))
                toast({
                    title: "Error",
                    description: "Failed to save: " + JSON.stringify(error),
                    variant: "destructive",
                })
            })
        }
    }

    const sharepointUrl = form.watch('sharepoint_url');

    const onSyncSharePoint = async () => {
        // plain fetch so we can show the server's error message
        const res = await fetch(`${API_URL}/integration/sharepoint/project/${project_id}/sync`, {
            method: "POST",
            headers: {Authorization: `Bearer ${accessToken}`},
        });
        const body = await res.json().catch(() => ({}));
        if (res.ok) {
            toast({title: "Sync started", description: "Bid files are being copied to SharePoint"});
        } else {
            toast({title: "Sync failed", description: body?.message || res.statusText, variant: "destructive"});
        }
    }

    const bidTabUrl = form.watch('bid_tab_url');
    // link + read every bid in the project against the bid tab (see backend/leveling).
    // runs as a background task on the server - useTask polls it and toasts when it finishes
    const bidTabTask = useTask(project_id, "leveling_sync", (task) => {
        if (task.status === "failed") {
            toast({title: "Bid tab sync failed", description: task.error ?? undefined, variant: "destructive"});
        } else {
            toast({title: `Synced ${task.result?.synced ?? 0} bids`, description: bidTabSummary(task.result)});
        }
    });

    const onSyncBidTab = async () => {
        try {
            await bidTabTask.start(`/projects/${project_id}/leveling/sync`);
        } catch (e: any) {
            toast({title: "Sync failed", description: e?.message, variant: "destructive"});
        }
    }

    if (projectContext?.role === CompanyType.builder) {
        return <div>You do not have permission to be here! </div>
    }

    return <div className={"container"}>
        <br/>
        <Card>
            <Form {...form} >
                <form onSubmit={(e) => {
                    form.handleSubmit(onSave)();
                }}>
                    <CardHeader>
                        <div className="flex columns-2 justify-between">
                            <CardTitle>Project Settings</CardTitle>
                            <Button
                                variant="outline"
                                type="submit"
                            >Save</Button>
                        </div>
                    </CardHeader>
                    <CardContent>

                        <div>
                            <strong className="space-y-2">Visibility</strong>
                            <FormToggleField
                                label={"Hidden - Invited Bidders Only "}
                                fname={"discoverability.invite_only"}
                                variant={"checkbox"} form={form}/>
                            <FormToggleField
                                label={"Visible to Previously Approved Bidders"}
                                fname={"discoverability.previously_approved"}
                                variant={"checkbox"}
                                description={"Including invited bidders"}
                                form={form}
                                />
                            <FormToggleField
                                label={"Fully Discoverable to Verified Bidders"}
                                fname={"discoverability.open_to_all"}
                                variant={"checkbox"}
                                form={form} />

                            {/*<FormToggleField*/}
                            {/*label={"Scopes are Enabled"}*/}
                            {/*fname={"smart_feedback"} variant={"checkbox"} form={form} disabled={true}/>*/}
                            <br/>
                        </div>
                        <strong className="space-y-4">Value Engineering (VE)</strong>
                        <FormToggleField
                            label={"Enable VE Module"}
                            fname={"ve.enabled"}
                            description={"VE is enabled"}
                            variant={"checkbox"}
                            form={form}/>
                        <FormToggleField
                            label={"Ranked Feedback"}
                            fname={"ve.feedback_enabled"}
                            description={"If multiple Bidders price a VE suggestion, they will see their rank on that price"}
                            variant={"checkbox"}
                            form={form}/>
                        <FormToggleField
                            label={"Share VE ideas with bidders"}
                            fname={"ve.enable_sharing"}
                            description={
                                "if Activited, you will be able to selectively share " +
                                "VE ideas to other bidders and get multiple prices"}
                            variant={"checkbox"}
                            form={form}/>
                        <FormToggleField
                            label={"All VE ideas are automatically shared"}
                            fname={"ve.enable_public"}
                            description={"If a Bidder suggests a VE idea, all other bidders will be able to see it."}
                            variant={"checkbox"}
                            form={form}/>
                        <br/>
                        <div className="space-y-4"><strong>Features</strong></div>
                        <FormToggleField
                            label={"Smart Feedback"}
                            fname={"smart_feedback"}
                            description={"Submitted bids get an AI review (bid documents + form) for you to edit and share with the bidder"}
                            variant={"checkbox"}
                            form={form}
                            disabled={false}/>
                        <FormToggleField
                            label={"Scopes are Enabled"}
                            fname={"scopes_enabled"}
                            variant={"checkbox"}
                            description={"Create multiple scopes per project (coming soon)"}
                            form={form}
                            disabled={false}/>
                        <br/>
                        <div className="space-y-4"><strong>SharePoint</strong></div>
                        <FormField
                            control={form.control}
                            name={"sharepoint_url"}
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>SharePoint Folder (sharing link)</FormLabel>
                                    <FormControl>
                                        <Input
                                            placeholder="https://yourorg.sharepoint.com/:f:/s/..."
                                            {...field}
                                            value={field.value ?? ''}/>
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <Button
                            className="mt-2"
                            variant="outline"
                            type="button"
                            disabled={!sharepointUrl}
                            onClick={onSyncSharePoint}
                        >Sync files to SharePoint</Button>
                        <FormField
                            control={form.control}
                            name={"bid_tab_url"}
                            render={({field}) => (
                                <FormItem className="mt-4">
                                    <FormLabel>Bid Tab Workbook (leveling)</FormLabel>
                                    <FormControl>
                                        <Input
                                            placeholder="https://yourorg.sharepoint.com/:x:/s/..."
                                            {...field}
                                            value={field.value ?? ''}/>
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <Button
                            className="mt-2"
                            variant="outline"
                            type="button"
                            disabled={!bidTabUrl || bidTabTask.running}
                            onClick={onSyncBidTab}
                        >{bidTabTask.running ? "Syncing..." : "Sync leveled amounts from bid tab"}</Button>
                        {bidTabTask.task && !bidTabTask.running &&
                            <div className="mt-1 text-xs text-gray-500">
                                Last sync {new Date(bidTabTask.task.finished_at ?? bidTabTask.task.started_at).toLocaleString()} by {bidTabTask.task.started_by}
                                {" - "}
                                {bidTabTask.task.status === "failed"
                                    ? <span className="text-red-600">failed: {bidTabTask.task.error}</span>
                                    : `${bidTabTask.task.result?.synced ?? 0} bids. ${bidTabSummary(bidTabTask.task.result) ?? ""}`}
                            </div>
                        }
                    </CardContent>
                </form>
            </Form>
        </Card>
        <br/>
    </div>
}

/**
 *
 *
 * */
function ProjectConfig() {
    // Builders not allowed to view this
    const projectContext = useContext(ProjectContext);
    if (projectContext?.role === CompanyType.builder) {
        return <ProjectBidInfo/>
    }

    return <Tabs defaultValue="bid_rounds">
        <TabsList>
            <TabsTrigger value="bid_rounds">Rounds</TabsTrigger>
            <TabsTrigger value="bid_setup">Bid Template</TabsTrigger>
            <TabsTrigger value="visibility">Project</TabsTrigger>
        </TabsList>

        <TabsContent value="bid_rounds"> <ListofRounds_v2/></TabsContent>
        <TabsContent value="bid_setup"><DevBidFormConfig/></TabsContent>
        <TabsContent value="visibility"><ProjectGlobals/></TabsContent>
    </Tabs>
}


export default withRequiredAuthInfo(ProjectConfig);
